import React from 'react';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useIntl } from 'react-intl';
import { memberDetailMessages } from './messages';
import {
  getDocumentExtractionV2ReviewState,
  DocumentExtractionV2ReviewState,
} from './ExtractedResourceProvenance';

type ExtractedResource = fhir.Resource & {
  extension?: fhir.Extension[];
  identifier?: Array<{ system?: string; value?: string }>;
};

const useStyles = makeStyles(theme => ({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.pxToRem(12),
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: theme.typography.caption.lineHeight,
    whiteSpace: 'nowrap',
  },
  needsReview: {
    backgroundColor: theme.palette.orange[100],
    color: theme.palette.orange[800],
    border: `1px solid ${theme.palette.orange[300]}`,
  },
  approved: {
    backgroundColor: theme.palette.green[100],
    color: theme.palette.green[800],
    border: `1px solid ${theme.palette.green[300]}`,
  },
}));

export const getDocumentExtractionReviewStatusLabel = (
  state: DocumentExtractionV2ReviewState,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string | null => {
  if (state === 'needs-review') {
    return formatMessage(
      memberDetailMessages.memberDocumentsExtractionReviewStatusNeedsReview,
    );
  }

  if (state === 'approved') {
    return formatMessage(
      memberDetailMessages.memberDocumentsExtractionReviewStatusApproved,
    );
  }

  return null;
};

export const DocumentExtractionReviewStatus: React.FC<{
  resource: ExtractedResource;
}> = ({ resource }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const state = getDocumentExtractionV2ReviewState(resource);
  const label = getDocumentExtractionReviewStatusLabel(state, formatMessage);

  if (!label) {
    return null;
  }

  return (
    <span
      className={`${classes.badge} ${
        state === 'approved' ? classes.approved : classes.needsReview
      }`}
    >
      {label}
    </span>
  );
};
